// src/components/admin/pagos/SolicitudesPagoTab.js
import React, { useEffect, useState, useCallback } from 'react'
import { Card, CardContent, Badge, Button } from '../../ui'
import { FileImage, FileText, CheckCircle2, XCircle, AlertTriangle, Clock, ShieldCheck, ShieldAlert, ShieldOff, Inbox } from 'lucide-react'
import { getSolicitudes } from '../../../lib/supabase'
import { useOrganization } from '../../../contexts/OrganizationContext'
import { formatMoney } from '../../../utils/helpers'
import ComprobanteViewer from './ComprobanteViewer'
import AprobarSolicitudModal from './AprobarSolicitudModal'
import RechazarSolicitudModal from './RechazarSolicitudModal'

const ESCANEO_BADGE = {
  pendiente: { label: 'Escaneando...', className: 'bg-yellow-100 text-yellow-800', Icon: Clock },
  limpio: { label: 'Limpio', className: 'bg-green-100 text-green-800', Icon: ShieldCheck },
  infectado: { label: 'Infectado: bloqueado', className: 'bg-red-100 text-red-800', Icon: ShieldAlert },
  error: { label: 'Error escaneo', className: 'bg-zinc-200 text-zinc-700', Icon: ShieldOff },
  no_escaneado: { label: 'Sin escaneo externo', className: 'bg-zinc-100 text-zinc-600', Icon: ShieldOff },
}

const SolicitudesPagoTab = () => {
  const { organization } = useOrganization()
  const [tab, setTab] = useState('pendiente')
  const [solicitudes, setSolicitudes] = useState([])
  const [loading, setLoading] = useState(true)
  const [comprobante, setComprobante] = useState(null)
  const [aprobar, setAprobar] = useState(null)
  const [rechazar, setRechazar] = useState(null)

  const load = useCallback(async () => {
    setLoading(true)
    const statuses = tab === 'pendiente' ? ['pendiente']
      : tab === 'aprobada' ? ['aprobada']
      : ['rechazada']
    const { data } = await getSolicitudes({ statuses })
    setSolicitudes(data)
    setLoading(false)
  }, [tab])

  useEffect(() => { load() }, [load])

  const onAfterAction = async () => {
    setAprobar(null)
    setRechazar(null)
    await load()
  }

  return (
    <div className="space-y-4">
      {/* El h2 repetia el titulo que ya pone el FeaturedLayout del wrapper.
          Los filtros por estado usan chips, como el resto de las bandejas. */}
      <div className="flex flex-wrap gap-1.5">
        {[
          { value: 'pendiente', label: 'Pendientes' },
          { value: 'aprobada', label: 'Aprobadas' },
          { value: 'rechazada', label: 'Rechazadas' },
        ].map((t) => (
          <Button
            key={t.value}
            variant={tab === t.value ? 'default' : 'outline'}
            size="sm"
            className="h-8 rounded-full px-3 text-xs font-normal"
            onClick={() => setTab(t.value)}
          >
            {t.label}
          </Button>
        ))}
      </div>

      <div>
        {loading && <p className="py-8 text-center text-sm text-muted-foreground">Cargando...</p>}
        {!loading && solicitudes.length === 0 && (
          <Card>
            <CardContent className="py-12 text-center">
              <Inbox className="h-12 w-12 mx-auto mb-3 text-muted-foreground opacity-30" />
              <p className="text-muted-foreground">
                {tab === 'pendiente'
                  ? 'No hay solicitudes pendientes'
                  : tab === 'aprobada'
                    ? 'Aún no has aprobado solicitudes'
                    : 'No hay solicitudes rechazadas'}
              </p>
              {tab === 'pendiente' && (
                <p className="mt-1 text-xs text-muted-foreground">
                  Aquí llegan los pagos que los padres envían desde su estado de cuenta.
                </p>
              )}
            </CardContent>
          </Card>
        )}
          <div className="space-y-3">
            {solicitudes.map((s) => {
              const eb = ESCANEO_BADGE[s.escaneo_estatus] || ESCANEO_BADGE.error
              const isInfectado = s.escaneo_estatus === 'infectado'
              const isPdf = s.comprobante_path?.toLowerCase().endsWith('.pdf')
              return (
                <Card key={s.id}>
                  <CardContent className="pt-4 space-y-3">
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div>
                        <p className="font-medium">
                          {s.jugador?.nombre_completo}
                          {s.jugador?.numero && <span className="text-xs text-muted-foreground ml-1">#{s.jugador.numero}</span>}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          Familia: {s.familia?.nombre} · Responsable: {s.familia?.responsable?.nombre_completo || '—'}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          Enviado: {new Date(s.created_at).toLocaleString('es-MX')}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-2xl font-bold">{formatMoney(s.monto_total)}</p>
                        <p className="text-xs text-muted-foreground">{s.metodo_pago}</p>
                        {s.referencia && <p className="text-[10px] text-muted-foreground">Ref: {s.referencia}</p>}
                      </div>
                    </div>

                    {s.notas && (
                      <p className="text-xs italic text-muted-foreground border-l-2 pl-2">Nota del papa: "{s.notas}"</p>
                    )}

                    {/* Cargos */}
                    <div className="bg-muted/40 rounded p-2 space-y-1">
                      {(s.cargos || []).map((sc) => (
                        <div key={sc.cargo_id} className="flex justify-between text-xs">
                          <span>{sc.pagos_cargos?.pagos_conceptos?.nombre || 'cargo'}</span>
                          <span className="font-medium">{formatMoney(sc.monto_propuesto)}</span>
                        </div>
                      ))}
                    </div>

                    {/* Comprobante + escaneo */}
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline" size="sm"
                        onClick={() => setComprobante({ path: s.comprobante_path, filename: `comprobante-${s.id}.${isPdf ? 'pdf' : 'jpg'}` })}
                      >
                        {isPdf ? <FileText className="h-4 w-4 mr-1" /> : <FileImage className="h-4 w-4 mr-1" />}
                        Ver comprobante
                      </Button>
                      <Badge className={`${eb.className} gap-1`}>
                        <eb.Icon className="h-3 w-3" /> {eb.label}
                      </Badge>
                    </div>

                    {s.replaces_solicitud_id && (
                      <p className="text-[10px] text-muted-foreground">
                        Correccion de solicitud #{s.replaces_solicitud_id.slice(0, 8)}
                      </p>
                    )}

                    {tab === 'rechazada' && s.rechazo_razon && (
                      <p className="text-xs text-red-700 bg-red-50 p-2 rounded">
                        <strong>Rechazada:</strong> {s.rechazo_razon}
                      </p>
                    )}

                    {tab === 'aprobada' && s.transaccion_id && (
                      <p className="text-xs text-green-700">
                        Transaccion creada #{s.transaccion_id.slice(0, 8)}
                      </p>
                    )}

                    {/* Acciones (solo pendientes) */}
                    {tab === 'pendiente' && (
                      <div className="flex gap-2 pt-2 border-t">
                        <Button
                          size="sm" disabled={isInfectado}
                          onClick={() => setAprobar(s)}
                        >
                          <CheckCircle2 className="h-4 w-4 mr-1" /> Aprobar
                        </Button>
                        <Button variant="destructive" size="sm" onClick={() => setRechazar(s)}>
                          <XCircle className="h-4 w-4 mr-1" /> Rechazar
                        </Button>
                        {isInfectado && (
                          <p className="text-xs text-red-700 self-center">
                            <AlertTriangle className="h-3 w-3 inline mr-1" />
                            Archivo malicioso, solo se puede rechazar
                          </p>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
              )
            })}
          </div>
      </div>

      <ComprobanteViewer
        open={!!comprobante}
        onOpenChange={(o) => !o && setComprobante(null)}
        path={comprobante?.path}
        filename={comprobante?.filename}
      />
      <AprobarSolicitudModal
        open={!!aprobar}
        onOpenChange={(o) => !o && setAprobar(null)}
        solicitud={aprobar}
        onSuccess={onAfterAction}
      />
      <RechazarSolicitudModal
        open={!!rechazar}
        onOpenChange={(o) => !o && setRechazar(null)}
        solicitud={rechazar}
        onSuccess={onAfterAction}
      />
    </div>
  )
}

export default SolicitudesPagoTab
